/**
 * The **card-duel battle gump** (trading card game, PRD #493 Slice 5, ADR-0286): the
 * in-match window where the two duelists see hands / boards / hero HP and play their turn.
 * Built on the shared UI-window chassis ({@link createUiWindow}, ADR-0271) and the shared
 * {@link renderCardFace} renderer (ADR-0271/Slice 3), so a card looks identical to the
 * Binder; wired through the card-duel feature installer (FeatureContext, ADR-0268).
 *
 * It holds **no game logic** (ADR-0268): it renders the server's authoritative, per-seat
 * {@link CardDuelStateMessage} (your hand full, the foe's hand a count) and sends
 * {@link CardDuelMove} intents back through the injected `onAction`. Legality (whose turn,
 * legal target, mana) is the server engine's - the gump only greys the obviously-illegal
 * (not your turn, unaffordable) and lets the server rule the rest, reverting on the next
 * authoritative state.
 *
 * `textContent`-only DOM (never `innerHTML`); a body-less document degrades to a no-op
 * handle (the window-module resilience rule).
 */
import { createUiWindow } from './ui/window';
import { cardFaceModel, renderCardFace, type CardFaceModel } from './cardFace';
import type {
  CardDuelStateMessage,
  WireCardBattleBeat,
  WireBattleCard,
  WireBattleSide,
  WireCardTargetCandidate,
} from './net/protocol';
import { COIN_CARD_ID, cardDef, coinCardDef, isKnownCard, keywordRule, scriptedEffectText, type CardDuelMove, type CardTarget } from './sim/index';
import type { EntityId } from './sim/entity';
import {
  BATTLE_SPRITE_UNIFORM_SCALE,
  CardBattleAnimator,
  MAX_BATTLE_SPRITE_HEIGHT,
  MAX_BATTLE_SPRITE_WIDTH,
} from './cardBattleAnimator';
import { cardSpriteArt } from './cardArt';
import cardBackUrl from '../assets/ui/back.png?url';
import type { SoundEvent } from './audio/soundObserver';

/** Injectable seams; `doc` defaults to the real document. */
export interface CardBattleDeps {
  doc?: Document;
  /** Send one in-match move up the socket (the installer wraps it as a `CardDuelAction`). */
  onAction: (move: CardDuelMove) => void;
  /**
   * Wall-clock source for the turn-timer extrapolation (Slice 7 polish, ADR-0289),
   * defaulting to `Date.now`. The server pushes `secondsRemaining` only on moves/turn
   * changes, so the readout is ticked down locally between pushes (the storm-board
   * anchor idiom); injectable so the jsdom test can drive the clock deterministically.
   */
  now?: () => number;
  /**
   * Render one live-world duelist look through the shared player-art path. `facing`
   * turns the stand pose inward ('east' for the left seat, 'west' for the right), so
   * the two duelists face each other across the arena.
   */
  renderPortrait?: (entity: EntityId, facing: PortraitFacing) => Promise<HTMLCanvasElement | null>;
  /** Present an accepted beat on the actual live-world player/Carl sprites. */
  onWorldBeat?: (state: CardDuelStateMessage, beat: WireCardBattleBeat) => void;
  /** Play one cue at the exact animation instant chosen by CardBattleAnimator. */
  onSound?: (cue: SoundEvent) => void;
}

/** Which way a duelist's arena sprite faces (toward the middle of the battlefield). */
export type PortraitFacing = 'east' | 'west';

/** The handle the installer drives the gump through. */
export interface CardBattleHandle {
  /** Render/refresh from an authoritative per-seat state (opens the gump on `open: true`). */
  setState(msg: CardDuelStateMessage): void;
  /** Force-close the gump (a match aborted with `open: false`). */
  close(): void;
  /** Whether the gump is currently shown. */
  isOpen(): boolean;
}

/** The player's current in-gump selection: nothing, a card awaiting a target, or an attacker. */
type Selection =
  | { kind: 'none' }
  | { kind: 'play-target'; instanceId: number }
  | { kind: 'attacker'; instanceId: number };

const NONE: Selection = { kind: 'none' };

/** Build a {@link CardFaceModel} from a wire battle card (its *current* runtime stats). */
function faceModel(c: WireBattleCard): CardFaceModel {
  // The Coin is match-transient (never set content, so `cardDef` cannot build
  // it); render it from its own definition like any other zero-cost item.
  const definition = isKnownCard(c.cardId)
    ? cardDef(c.cardId, c.rarity)
    : c.cardId === COIN_CARD_ID
      ? coinCardDef(c.rarity)
      : undefined;
  if (definition) {
    const authored = cardFaceModel(definition);
    return {
      ...authored,
      name: c.name,
      cost: c.cost,
      attack: c.kind === 'monster' ? c.attack : undefined,
      health: c.kind === 'monster' ? c.health : undefined,
    };
  }
  return {
    cardId: c.cardId,
    name: c.name,
    rarity: c.rarity,
    cost: c.cost,
    kind: c.kind,
    attack: c.kind === 'monster' ? c.attack : undefined,
    health: c.kind === 'monster' ? c.health : undefined,
    detail: c.keywords.length > 0 ? c.keywords.map(cap).join(', ') : undefined,
  };
}

/**
 * Project the shared card artwork into battlefield-only chrome.
 *
 * The card face is useful as the one source of creature crops and hues, but a minion
 * must never retain its rarity frame, mana orb, type line, or rules panel in the arena.
 */
function renderBattlefieldActor(doc: Document, card: WireBattleCard): HTMLElement {
  const model = faceModel(card);
  const actor = renderCardFace(doc, model);
  actor.classList.remove('card-face');
  actor.classList.add('card-battle-minion');
  actor.querySelectorAll('.card-cost, .card-kind, .card-detail, .card-count').forEach((node) => node.remove());
  // The card face inflates every crop to fill its art box; on the battlefield the
  // static crop instead takes the same uniform native-pixel scale as the animated
  // clips, so creatures keep their in-world relative sizes.
  if (model.body !== undefined) {
    const sprite = cardSpriteArt(model.body);
    const crop = actor.querySelector<HTMLElement>('.card-art-sprite');
    if (sprite && crop) {
      const scale = Math.min(
        BATTLE_SPRITE_UNIFORM_SCALE,
        MAX_BATTLE_SPRITE_WIDTH / sprite.width,
        MAX_BATTLE_SPRITE_HEIGHT / sprite.height,
      );
      crop.style.width = `${Math.round(sprite.width * scale)}px`;
    }
  }
  if (card.kind === 'monster') {
    const bar = el(doc, 'div', 'card-battle-minion-health-bar');
    bar.setAttribute('aria-hidden', 'true');
    const fill = el(doc, 'div', 'card-battle-minion-health-fill');
    const percent = card.maxHealth > 0
      ? Math.max(0, Math.min(100, card.health / card.maxHealth * 100))
      : 0;
    fill.style.width = `${percent}%`;
    bar.appendChild(fill);
    actor.appendChild(bar);
    if (card.health < card.maxHealth) actor.classList.add('wounded');
  }
  if (card.keywords.length > 0) {
    const chips = el(doc, 'div', 'card-battle-minion-keywords');
    for (const keyword of card.keywords) {
      const chip = el(doc, 'span', 'card-battle-minion-keyword');
      chip.textContent = cap(keyword);
      chips.appendChild(chip);
    }
    actor.appendChild(chips);
  }
  actor.appendChild(renderMinionTooltip(doc, card));
  return actor;
}

/**
 * The hover card for one battlefield minion: name, live stats, and every keyword
 * spelled out with its rules text (plus the def's scripted triggers), so the arena
 * never needs to cram rules onto the actor itself.
 */
function renderMinionTooltip(doc: Document, card: WireBattleCard): HTMLElement {
  const tip = el(doc, 'div', 'card-battle-minion-tooltip');
  tip.setAttribute('aria-hidden', 'true');
  const name = el(doc, 'div', 'card-battle-minion-tooltip-name');
  name.textContent = card.name;
  tip.appendChild(name);
  if (card.kind === 'monster') {
    const stats = el(doc, 'div', 'card-battle-minion-tooltip-stats');
    stats.textContent = `${card.attack} attack · ${card.health}/${card.maxHealth} health`;
    tip.appendChild(stats);
  }
  const def = isKnownCard(card.cardId) ? cardDef(card.cardId, card.rarity) : undefined;
  for (const keyword of card.keywords) {
    const line = el(doc, 'div', 'card-battle-minion-tooltip-line');
    const rule = keywordRule(keyword, def?.kind === 'monster' ? def.abilities.heal : undefined);
    line.textContent = rule ? `${cap(keyword)}: ${rule}` : cap(keyword);
    tip.appendChild(line);
  }
  if (def?.kind === 'monster') {
    if (def.abilities.onPlay) {
      const line = el(doc, 'div', 'card-battle-minion-tooltip-line');
      line.textContent = `On play: ${scriptedEffectText(def.abilities.onPlay)}.`;
      tip.appendChild(line);
    }
    if (def.abilities.onDeath) {
      const line = el(doc, 'div', 'card-battle-minion-tooltip-line');
      line.textContent = `On death: ${scriptedEffectText(def.abilities.onDeath)}.`;
      tip.appendChild(line);
    }
    if (def.abilities.endOfTurn) {
      const line = el(doc, 'div', 'card-battle-minion-tooltip-line');
      line.textContent = `End of turn: ${scriptedEffectText(def.abilities.endOfTurn)}.`;
      tip.appendChild(line);
    }
  }
  return tip;
}

function cap(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

/** How often the local turn-timer readout is re-derived between server pushes (Slice 7). */
const CLOCK_TICK_MS = 500;

export function initCardBattle(deps: CardBattleDeps): CardBattleHandle {
  const doc = deps.doc ?? document;
  const now = deps.now ?? (() => Date.now());
  let onWindowClose = (): void => {};

  const noop: CardBattleHandle = {
    setState: () => {},
    close: () => {},
    isOpen: () => false,
  };

  const win = createUiWindow({
    doc,
    id: 'card-battle',
    title: 'Card Duel',
    ariaLabel: 'Card Duel',
    fallbackWidth: 1160,
    fallbackHeight: 650,
    centerVertically: true,
    onClose: () => onWindowClose(),
    // No Escape-close: a stray Escape mid-battle should not blind the player to the match.
  });
  if (!win) return noop;

  const minimizedBtn = button(doc, 'Card Duel');
  minimizedBtn.id = 'card-battle-minimized';
  minimizedBtn.className = 'card-battle-minimized';
  minimizedBtn.hidden = true;
  minimizedBtn.setAttribute('aria-label', 'Restore Card Duel');
  minimizedBtn.title = 'Restore Card Duel';
  doc.body?.appendChild(minimizedBtn);

  // Build the content once into the window root (component-owned DOM, ADR-0271).
  const body = doc.createElement('div');
  body.id = 'card-battle-body';
  const statusEl = el(doc, 'div', 'card-battle-status');
  statusEl.setAttribute('role', 'status');
  const battlefield = el(doc, 'section', 'card-battle-battlefield card-battle-shared-arena');
  battlefield.setAttribute('aria-label', 'Battlefield');
  const battlefieldTitle = el(doc, 'div', 'card-battle-battlefield-title');
  battlefieldTitle.textContent = 'Battlefield';
  const foePanel = el(doc, 'div', 'card-battle-foe');
  const foeBoard = el(doc, 'div', 'card-battle-board card-battle-foe-board');
  const youBoard = el(doc, 'div', 'card-battle-board card-battle-you-board');
  const youPanel = el(doc, 'div', 'card-battle-you');
  const fieldDivider = el(doc, 'div', 'card-battle-field-divider');
  fieldDivider.setAttribute('aria-hidden', 'true');
  battlefield.append(battlefieldTitle, foePanel, foeBoard, fieldDivider, youPanel, youBoard);
  // The foe's hand as a fan of card backs (the count itself lives on their hero plaque).
  const foeHandEl = el(doc, 'div', 'card-battle-foe-hand');
  foeHandEl.setAttribute('aria-hidden', 'true');
  const handDock = el(doc, 'section', 'card-battle-hand-dock');
  handDock.setAttribute('aria-label', 'Your hand');
  const handEl = el(doc, 'div', 'card-battle-hand');
  handDock.append(handEl);
  const logPanel = el(doc, 'aside', 'card-battle-log-panel');
  logPanel.setAttribute('aria-label', 'Battle log');
  const logTitle = el(doc, 'div', 'card-battle-log-title');
  logTitle.textContent = 'Battle log';
  const logEl = el(doc, 'div', 'card-battle-log');
  const logAnnouncer = el(doc, 'span', 'sr-only');
  logAnnouncer.setAttribute('aria-live', 'polite');
  logAnnouncer.setAttribute('aria-atomic', 'true');
  logPanel.append(logTitle, logEl, logAnnouncer);
  const hintEl = el(doc, 'div', 'card-battle-hint');
  hintEl.setAttribute('role', 'status');
  const targetChooser = el(doc, 'section', 'card-battle-target-chooser');
  targetChooser.setAttribute('role', 'dialog');
  targetChooser.setAttribute('aria-label', 'Choose a fallen minion');
  targetChooser.hidden = true;
  const actionsEl = el(doc, 'div', 'card-battle-actions');
  const endTurnBtn = button(doc, 'End turn');
  actionsEl.append(endTurnBtn);
  const optionsEl = el(doc, 'div', 'card-battle-options');
  const optionsToggle = button(doc, '...');
  optionsToggle.className = 'card-battle-options-toggle';
  optionsToggle.setAttribute('aria-label', 'Duel options');
  optionsToggle.setAttribute('aria-haspopup', 'menu');
  optionsToggle.setAttribute('aria-expanded', 'false');
  optionsToggle.title = 'Duel options';
  const optionsMenu = el(doc, 'div', 'card-battle-options-menu');
  optionsMenu.setAttribute('role', 'menu');
  optionsMenu.hidden = true;
  const concedeBtn = button(doc, 'Concede duel...');
  concedeBtn.classList.add('card-battle-concede');
  concedeBtn.setAttribute('role', 'menuitem');
  optionsMenu.append(concedeBtn);
  optionsEl.append(optionsToggle, optionsMenu);
  doc.getElementById('card-battle-bar')?.append(optionsEl);
  const resultEl = el(doc, 'div', 'card-battle-result');
  resultEl.hidden = true;
  const resultText = el(doc, 'div', 'card-battle-result-text');
  const closeBtn = button(doc, 'Close');
  resultEl.append(resultText, closeBtn);
  const concedeConfirmation = el(doc, 'section', 'card-battle-concede-confirmation');
  concedeConfirmation.setAttribute('role', 'alertdialog');
  concedeConfirmation.setAttribute('aria-modal', 'true');
  concedeConfirmation.setAttribute('aria-labelledby', 'card-battle-concede-title');
  concedeConfirmation.setAttribute('aria-describedby', 'card-battle-concede-description');
  concedeConfirmation.hidden = true;
  const concedeTitle = el(doc, 'h3', 'card-battle-concede-title');
  concedeTitle.id = 'card-battle-concede-title';
  concedeTitle.textContent = 'Concede this duel?';
  const concedeDescription = el(doc, 'p', 'card-battle-concede-description');
  concedeDescription.id = 'card-battle-concede-description';
  concedeDescription.textContent = 'Conceding counts as a loss and ends the duel immediately.';
  const concedeConfirmationActions = el(doc, 'div', 'card-battle-concede-confirmation-actions');
  const concedeCancelBtn = button(doc, 'Keep playing');
  concedeCancelBtn.classList.add('card-battle-concede-cancel');
  const concedeConfirmBtn = button(doc, 'Concede duel');
  concedeConfirmBtn.classList.add('card-battle-concede-confirm');
  concedeConfirmationActions.append(concedeCancelBtn, concedeConfirmBtn);
  concedeConfirmation.append(concedeTitle, concedeDescription, concedeConfirmationActions);
  const footer = el(doc, 'div', 'card-battle-footer');
  footer.append(hintEl, actionsEl);
  const table = el(doc, 'div', 'card-battle-table');
  table.append(foeHandEl, battlefield, handDock, footer);
  const main = el(doc, 'div', 'card-battle-main');
  main.append(table, logPanel);
  body.append(statusEl, main, targetChooser, resultEl, concedeConfirmation);
  win.root.appendChild(body);
  const animator = new CardBattleAnimator(doc, battlefield, deps.onWorldBeat, deps.onSound);

  let state: CardDuelStateMessage | null = null;
  let announcedLogSequence = -1;
  let selection: Selection = NONE;
  let mulliganSelection = new Set<number>();
  // Turn-clock extrapolation (Slice 7 polish, ADR-0289): the server pushes
  // `secondsRemaining` only on moves/turn changes, so between pushes the client counts
  // it down off a wall-clock anchor taken at the last push. `clockTimer` re-derives just
  // the status line on a steady cadence while a live match is shown; cleared on close /
  // match end so no timer leaks.
  let clockAnchorMs = 0;
  let clockAnchorSeconds = 0;
  let clockTimer: ReturnType<typeof setInterval> | undefined;
  let minimizedByUser = false;
  let forceClosing = false;

  const foeSeat = (): 0 | 1 => (state && state.yourSeat === 0 ? 1 : 0);

  /** The seconds to show now: the last authoritative value minus wall-clock elapsed since. */
  const displaySeconds = (): number => {
    const elapsed = Math.floor((now() - clockAnchorMs) / 1000);
    return Math.max(0, clockAnchorSeconds - elapsed);
  };

  /** Render just the status line (turn + countdown), reused by the render pass and the clock tick. */
  const renderStatus = (): void => {
    if (!state) return;
    minimizedBtn.classList.toggle('your-turn', state.phase === 'playing' && state.yourTurn);
    if (state.phase === 'ended') {
      statusEl.textContent = 'The card duel has ended.';
      minimizedBtn.textContent = 'Card Duel · Result ready';
      return;
    }
    const sec = displaySeconds();
    if (state.phase === 'mulligan') {
      const text = state.mulliganSubmitted ? 'Opening hand locked' : 'Choose your opening hand';
      statusEl.textContent = `${text} · ${sec}s`;
      minimizedBtn.textContent = `Card Duel · ${state.opponentName} · ${text} · ${sec}s`;
      return;
    }
    const turn = state.yourTurn ? 'Your turn' : `${state.opponentName}'s turn`;
    const stake = state.wager > 0 ? ` · ${state.wager} gold` : '';
    statusEl.textContent = `Turn ${state.turnNumber} · ${turn} · ${sec}s${stake}`;
    minimizedBtn.textContent = `Card Duel · ${state.opponentName} · ${turn} · ${sec}s`;
  };

  const stopClock = (): void => {
    if (clockTimer !== undefined) {
      clearInterval(clockTimer);
      clockTimer = undefined;
    }
  };

  const send = (move: CardDuelMove): void => {
    selection = NONE;
    deps.onAction(move);
  };

  /** A hero/minion target was clicked while a card or attacker is selected. */
  const pickTarget = (target: CardTarget): void => {
    if (!state) return;
    if (selection.kind === 'play-target') {
      send({ kind: 'PlayCard', instanceId: selection.instanceId, target });
    } else if (selection.kind === 'attacker') {
      send({ kind: 'Attack', attackerId: selection.instanceId, target });
    }
    render();
  };

  const selectedCard = (): WireBattleCard | undefined => {
    if (selection.kind !== 'play-target') return undefined;
    const instanceId = selection.instanceId;
    return state?.hand.find((card) => card.instanceId === instanceId);
  };

  const sameTarget = (a: CardTarget, b: WireCardTargetCandidate): boolean =>
    a.kind === b.kind
    && (a.kind === 'hero'
      ? b.kind === 'hero' && a.seat === b.seat
      : b.kind !== 'hero' && a.instanceId === b.instanceId);

  const isLegalPlayTarget = (target: CardTarget): boolean => {
    const candidates = selectedCard()?.legalTargets;
    // Compatibility with an older directed state: the authoritative engine
    // still rejects an illegal pick. Current servers always send candidates.
    return candidates === undefined || candidates.some((candidate) => sameTarget(target, candidate));
  };

  /** Click a hand card: play a monster/untargeted spell at once, or arm a targeted spell. */
  const clickHandCard = (card: WireBattleCard): void => {
    if (!state || state.phase === 'ended') return;
    if (state.phase === 'mulligan') {
      if (state.mulliganSubmitted) return;
      if (mulliganSelection.has(card.instanceId)) mulliganSelection.delete(card.instanceId);
      else mulliganSelection.add(card.instanceId);
      render();
      return;
    }
    // Even an obviously illegal attempt goes to the authoritative engine. Its rejection
    // comes back as a private battle-log line instead of a click that appears to do nothing.
    if (card.needsTarget) {
      selection = { kind: 'play-target', instanceId: card.instanceId };
      render();
      return;
    }
    send({ kind: 'PlayCard', instanceId: card.instanceId });
    render();
  };

  /** Click one of your board minions: arm it as the attacker (if it can still attack). */
  const clickYourMinion = (card: WireBattleCard): void => {
    if (!state || !state.yourTurn || !card.canAttack || card.attack <= 0) return;
    selection =
      selection.kind === 'attacker' && selection.instanceId === card.instanceId
        ? NONE
        : { kind: 'attacker', instanceId: card.instanceId };
    render();
  };

  const render = (): void => {
    if (!state) return;
    const you = state.you;
    const foe = state.foe;

    // Status line (turn + countdown), ticked locally between pushes by `clockTimer`.
    renderStatus();

    // Foe hero panel (their hand is a count only).
    renderSide(foePanel, `${state.opponentName}`, state.opponentId, foe, {
      handLabel: `${foe.handCount} in hand`,
      facing: 'west',
      onHeroClick: canTargetFoeHero() ? () => pickTarget({ kind: 'hero', seat: foeSeat() }) : undefined,
    });
    // Your hero panel.
    renderSide(youPanel, 'You', state.youId, you, {
      handLabel: `${you.handCount} in hand`,
      facing: 'east',
      onHeroClick: canTargetOwnHero() ? () => pickTarget({ kind: 'hero', seat: state!.yourSeat }) : undefined,
    });

    // Boards. Foe board: clickable as an attack/spell target; your board: clickable to attack with.
    renderBoard(foeBoard, foe.board, 'foe', (c) =>
      canTargetMinion(c, 'foe') ? pickTarget({ kind: 'minion', instanceId: c.instanceId }) : undefined,
    );
    renderBoard(youBoard, you.board, 'you', (c) => {
      if (canTargetMinion(c, 'you')) {
        pickTarget({ kind: 'minion', instanceId: c.instanceId });
        return;
      }
      clickYourMinion(c);
    }, (c) => selection.kind === 'attacker' && selection.instanceId === c.instanceId);

    // Hands.
    renderFoeHand(foe.handCount);
    renderHand();
    renderTargetChooser();

    // Persistent server-authored transcript. Repeat the turn heading only when it changes.
    logEl.replaceChildren();
    let shownTurn = -1;
    for (const entry of state.log) {
      if (entry.turn !== shownTurn) {
        const turnHeading = el(doc, 'div', 'card-battle-log-turn');
        turnHeading.textContent = `Turn ${entry.turn}`;
        logEl.appendChild(turnHeading);
        shownTurn = entry.turn;
      }
      const line = el(doc, 'div', `card-battle-log-entry ${entry.tone}`);
      line.textContent = entry.text;
      logEl.appendChild(line);
    }
    logEl.scrollTop = logEl.scrollHeight;
    const newestLogEntry = state.log.at(-1);
    if (newestLogEntry) {
      if (newestLogEntry.sequence !== announcedLogSequence) {
        announcedLogSequence = newestLogEntry.sequence;
        logAnnouncer.textContent = newestLogEntry.text;
      }
    }

    // Hint.
    if (state.phase === 'ended') hintEl.textContent = '';
    else if (state.phase === 'mulligan') {
      hintEl.textContent = state.mulliganSubmitted
        ? 'Opening hand locked. Waiting for your opponent…'
        : 'Select any cards to replace, then confirm your opening hand.';
    }
    else if (state.autoSkipPending) hintEl.textContent = 'No playable moves. Your turn will be skipped.';
    else if (selection.kind === 'play-target') hintEl.textContent = 'Choose a target for the card.';
    else if (selection.kind === 'attacker') hintEl.textContent = 'Choose what to attack.';
    else if (state.yourTurn) hintEl.textContent = 'Play a card, or attack with a minion.';
    else hintEl.textContent = 'Waiting for your opponent…';

    // Actions.
    endTurnBtn.textContent = state.phase === 'mulligan'
      ? state.mulliganSubmitted
        ? 'Hand locked'
        : (mulliganSelection.size === 0 ? 'Keep hand' : `Replace ${mulliganSelection.size}`)
      : 'End turn';
    endTurnBtn.disabled = state.phase === 'mulligan'
      ? state.mulliganSubmitted
      : !state.yourTurn || state.phase === 'ended';
    optionsEl.hidden = state.phase === 'ended';
    if (state.phase === 'ended') {
      optionsMenu.hidden = true;
      optionsToggle.setAttribute('aria-expanded', 'false');
      concedeConfirmation.hidden = true;
    }

    animator.observe(state);

    // Result overlay. A lethal accepted attack gets to finish before the opaque result
    // treatment appears, so the last combat action is never hidden by authoritative cleanup.
    if (state.phase === 'ended') {
      resultText.textContent =
        state.outcome === 'win'
          ? 'You won the card duel!'
          : state.outcome === 'loss'
            ? 'You lost the card duel.'
            : 'The card duel was a draw.';
      if (animator.isIdle()) {
        resultEl.hidden = false;
      } else {
        resultEl.hidden = true;
        const renderedState = state;
        void animator.whenIdle().then(() => {
          if (state === renderedState && state.phase === 'ended' && win.isOpen()) {
            resultEl.hidden = false;
          }
        });
      }
    } else {
      resultEl.hidden = true;
    }
  };

  const isPickingTarget = (): boolean =>
    selection.kind === 'play-target' || selection.kind === 'attacker';
  const canTargetFoeHero = (): boolean =>
    selection.kind === 'attacker'
    || (selection.kind === 'play-target' && isLegalPlayTarget({ kind: 'hero', seat: foeSeat() }));
  // Only a targeted spell may hit your own hero (a friendly buff / self-heal); an attacker never can.
  const canTargetOwnHero = (): boolean =>
    selection.kind === 'play-target'
    && state !== null
    && isLegalPlayTarget({ kind: 'hero', seat: state.yourSeat });
  const canTargetMinion = (card: WireBattleCard, side: 'you' | 'foe'): boolean =>
    (selection.kind === 'attacker' && side === 'foe')
    || (selection.kind === 'play-target'
      && isLegalPlayTarget({ kind: 'minion', instanceId: card.instanceId }));

  const renderSide = (
    container: HTMLElement,
    name: string,
    entity: EntityId,
    side: WireBattleSide,
    opts: { handLabel: string; facing: PortraitFacing; onHeroClick?: () => void },
  ): void => {
    const previous = container.querySelector<HTMLElement>('.card-battle-hero');
    const hero = el(doc, 'div', 'card-battle-hero');
    if (opts.onHeroClick) {
      hero.classList.add('targetable');
      hero.tabIndex = 0;
      hero.setAttribute('role', 'button');
      hero.setAttribute('aria-label', `Target ${name}, ${side.heroHealth} health`);
      hero.onclick = opts.onHeroClick;
      hero.onkeydown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        opts.onHeroClick?.();
      };
    } else {
      hero.onclick = null;
    }
    const portrait = el(doc, 'div', 'card-battle-hero-portrait');
    portrait.setAttribute('aria-hidden', 'true');
    portrait.dataset.facing = opts.facing;
    if (deps.renderPortrait) {
      void deps.renderPortrait(entity, opts.facing).then((canvas) => {
        if (canvas && portrait.isConnected) portrait.replaceChildren(canvas);
      }).catch(() => {});
    }
    const identity = el(doc, 'div', 'card-battle-hero-identity');
    const nameEl = el(doc, 'span', 'card-battle-hero-name');
    nameEl.textContent = name;
    const healthBar = el(doc, 'div', 'card-battle-health-bar');
    healthBar.setAttribute('role', 'progressbar');
    healthBar.setAttribute('aria-label', `${name} health`);
    healthBar.setAttribute('aria-valuemin', '0');
    healthBar.setAttribute('aria-valuemax', String(side.heroMaxHealth));
    healthBar.setAttribute('aria-valuenow', String(side.heroHealth));
    const healthFill = el(doc, 'div', 'card-battle-health-fill');
    const healthPercent = side.heroMaxHealth > 0
      ? Math.max(0, Math.min(100, side.heroHealth / side.heroMaxHealth * 100))
      : 0;
    healthFill.style.width = `${healthPercent}%`;
    healthBar.appendChild(healthFill);
    const hpEl = el(doc, 'span', 'card-battle-hero-hp');
    hpEl.textContent = `❤ ${side.heroHealth}/${side.heroMaxHealth}`;
    const manaEl = el(doc, 'span', 'card-battle-hero-mana');
    manaEl.textContent = `✦ ${side.mana}/${side.maxMana}`;
    const zonesEl = el(doc, 'span', 'card-battle-hero-zones');
    zonesEl.textContent = `${opts.handLabel} · ${side.deckCount} in deck`;
    const wardsEl = el(doc, 'span', 'card-battle-wards');
    const wards = [
      side.reactiveArmor !== undefined ? `Reactive Armor ${side.reactiveArmor}` : '',
      side.magicReflection ? 'Magic Reflection' : '',
      side.bloodOath ? 'Blood Oath' : '',
    ].filter(Boolean);
    wardsEl.textContent = wards.join(' | ');
    wardsEl.hidden = wards.length === 0;
    identity.append(nameEl, healthBar, hpEl, manaEl, zonesEl, wardsEl);
    hero.append(portrait, identity);
    if (previous?.dataset.battleRetain === 'true') {
      const hit = previous.classList.contains('card-battle-hit');
      previous.className = hero.className;
      if (hit) previous.classList.add('card-battle-hit');
      previous.onclick = hero.onclick;
      previous.replaceChildren(...hero.childNodes);
      container.replaceChildren(previous);
    } else {
      container.replaceChildren(hero);
    }
  };

  const renderBoard = (
    container: HTMLElement,
    cards: WireBattleCard[],
    side: 'you' | 'foe',
    onClick: (c: WireBattleCard) => void,
    isSelected?: (c: WireBattleCard) => boolean,
  ): void => {
    const previousActors = [...container.querySelectorAll<HTMLElement>('[data-instance-id]')];
    const previous = new Map(
      previousActors.map((actor) => [Number(actor.dataset.instanceId), actor] as const),
    );
    const rendered: HTMLElement[] = [];
    for (const c of cards) {
      const face = renderBattlefieldActor(doc, c);
      face.dataset.instanceId = String(c.instanceId);
      face.dataset.side = side;
      face.dataset.facing = side === 'you' ? 'north' : 'south';
      if (c.keywords.includes('taunt')) face.classList.add('has-taunt');
      if (isSelected?.(c)) face.classList.add('selected');
      if (state?.yourTurn && container === youBoard && c.canAttack && c.attack > 0) {
        face.classList.add('ready');
      }
      if (canTargetMinion(c, side)) {
        face.classList.add('targetable');
        face.tabIndex = 0;
        face.setAttribute('role', 'button');
        face.setAttribute('aria-label', `Target ${c.name}, ${c.attack} attack, ${c.health} health`);
        face.onkeydown = (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          onClick(c);
        };
      } else if (isPickingTarget()) {
        face.classList.add('not-targetable');
        face.setAttribute('aria-disabled', 'true');
      }
      face.onclick = () => onClick(c);
      const current = previous.get(c.instanceId);
      rendered.push(current ? reconcileMinion(current, face) : face);
      previous.delete(c.instanceId);
    }
    // A dying minion animates out from its own slot: walk the old DOM order and
    // re-insert each retained ghost after the survivors that stood to its left,
    // so the row never reflows mid-animation (a far-left victim must not jump to
    // the far-right end of the board).
    let survivorsToLeft = 0;
    let ghostsInserted = 0;
    for (const actor of previousActors) {
      const id = Number(actor.dataset.instanceId);
      if (!previous.has(id)) {
        survivorsToLeft++;
        continue;
      }
      if (actor.dataset.battleRetain !== 'true') continue;
      actor.dataset.battleGhost = 'true';
      actor.setAttribute('aria-hidden', 'true');
      actor.style.pointerEvents = 'none';
      rendered.splice(survivorsToLeft + ghostsInserted, 0, actor);
      ghostsInserted++;
    }
    if (rendered.length === 0) {
      const empty = el(doc, 'span', 'card-battle-board-empty');
      empty.textContent = '—';
      rendered.push(empty);
    }
    container.replaceChildren(...rendered);
  };

  /**
   * The foe's hidden hand: one card back per card, fanned along the opposite arc
   * of your own hand (edges rise instead of dip - the fan seen from the far side).
   */
  const renderFoeHand = (count: number): void => {
    foeHandEl.replaceChildren();
    const centre = (count - 1) / 2;
    const radius = Math.max(centre, 1);
    for (let index = 0; index < count; index++) {
      const back = el(doc, 'div', 'card-battle-foe-hand-card');
      const art = doc.createElement('img');
      art.src = cardBackUrl;
      art.alt = '';
      art.draggable = false;
      back.appendChild(art);
      const distance = index - centre;
      const normalized = distance / radius;
      back.style.setProperty('--hand-rotation', `${formatLayoutNumber(normalized * -8)}deg`);
      back.style.setProperty('--hand-curve-y', `${Math.round(normalized * normalized * -12)}px`);
      foeHandEl.appendChild(back);
    }
  };

  const renderHand = (): void => {
    handEl.replaceChildren();
    if (!state) return;
    if (state.hand.length === 0) {
      const empty = el(doc, 'span', 'card-battle-board-empty');
      empty.textContent = 'Your hand is empty.';
      handEl.appendChild(empty);
      return;
    }
    const centre = (state.hand.length - 1) / 2;
    const radius = Math.max(centre, 1);
    for (const [index, c] of state.hand.entries()) {
      const model = faceModel(c);
      const face = renderCardFace(doc, model);
      face.classList.add('card-battle-hand-card');
      // The slot owns the fan layout and never moves; only the face inside it lifts
      // on hover. A moving hover target un-hovers itself mid-lift and oscillates.
      const slot = el(doc, 'div', 'card-battle-hand-slot');
      const distance = index - centre;
      const normalized = distance / radius;
      slot.style.setProperty('--hand-rotation', `${formatLayoutNumber(normalized * 10)}deg`);
      slot.style.setProperty('--hand-curve-y', `${Math.round(normalized * normalized * 18)}px`);
      slot.style.setProperty('--hand-stack', String(Math.round(centre + 1 - Math.abs(distance))));
      face.tabIndex = 0;
      face.setAttribute('role', 'button');
      const mulliganSelected = state.phase === 'mulligan' && mulliganSelection.has(c.instanceId);
      face.setAttribute('aria-label', state.phase === 'mulligan'
        ? `${mulliganSelected ? 'Keep' : 'Replace'} ${c.name}, ${c.cost} mana`
        : `Play ${c.name}, ${c.cost} mana${model.detail ? `. ${model.detail}` : ''}`);
      const hasCandidate = !c.needsTarget
        || c.legalTargets === undefined
        || c.legalTargets.length > 0;
      const playable = state.phase === 'mulligan'
        ? !state.mulliganSubmitted
        : state.yourTurn && state.phase === 'playing' && c.cost <= state.you.mana && hasCandidate;
      if (!playable) face.classList.add('disabled');
      if (mulliganSelected) face.classList.add('selected');
      if (selection.kind === 'play-target' && selection.instanceId === c.instanceId) {
        face.classList.add('selected');
      }
      face.onclick = () => clickHandCard(c);
      face.onkeydown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        clickHandCard(c);
      };
      slot.appendChild(face);
      handEl.appendChild(slot);
    }
  };

  const renderTargetChooser = (): void => {
    targetChooser.replaceChildren();
    const corpses = selectedCard()?.legalTargets?.filter(
      (target): target is Extract<WireCardTargetCandidate, { kind: 'graveyard' }> =>
        target.kind === 'graveyard',
    ) ?? [];
    if (selection.kind !== 'play-target' || corpses.length === 0) {
      targetChooser.hidden = true;
      return;
    }
    targetChooser.hidden = false;
    const title = el(doc, 'h3', 'card-battle-target-title');
    title.textContent = 'Choose a fallen minion';
    const list = el(doc, 'div', 'card-battle-graveyard-list');
    for (const corpse of corpses) {
      const choice = button(
        doc,
        `${corpse.name} · ${corpse.attack}/${corpse.health}`
        + `${corpse.keywords.length ? ` · ${corpse.keywords.map(cap).join(', ')}` : ''}`
        + ` · death ${corpse.deathOrder + 1}`,
      );
      choice.classList.add('card-battle-graveyard-choice');
      choice.setAttribute(
        'aria-label',
        `${corpse.name}, ${corpse.attack} attack, ${corpse.health} health, death order ${corpse.deathOrder + 1}`,
      );
      choice.onclick = () => pickTarget({ kind: 'graveyard', instanceId: corpse.instanceId });
      list.appendChild(choice);
    }
    const cancel = button(doc, 'Cancel');
    cancel.onclick = () => {
      selection = NONE;
      render();
    };
    targetChooser.append(title, list, cancel);
  };

  endTurnBtn.onclick = () => {
    if (!state) return;
    if (state.phase === 'mulligan' && !state.mulliganSubmitted) {
      send({ kind: 'Mulligan', instanceIds: [...mulliganSelection] });
      return;
    }
    if (state.yourTurn && state.phase === 'playing') send({ kind: 'EndTurn' });
  };
  const closeOptions = (): void => {
    optionsMenu.hidden = true;
    optionsToggle.setAttribute('aria-expanded', 'false');
  };
  const closeConcedeConfirmation = (restoreFocus: boolean): void => {
    concedeConfirmation.hidden = true;
    if (restoreFocus) optionsToggle.focus();
  };
  optionsEl.addEventListener('pointerdown', (event) => event.stopPropagation());
  optionsToggle.onclick = () => {
    const opening = optionsMenu.hidden;
    optionsMenu.hidden = !opening;
    optionsToggle.setAttribute('aria-expanded', String(opening));
    if (opening) concedeBtn.focus();
  };
  concedeBtn.onclick = () => {
    if (!state || state.phase !== 'playing') return;
    closeOptions();
    concedeConfirmation.hidden = false;
    concedeCancelBtn.focus();
  };
  concedeCancelBtn.onclick = () => closeConcedeConfirmation(true);
  concedeConfirmBtn.onclick = () => {
    if (state && state.phase === 'playing') send({ kind: 'Concede' });
    closeConcedeConfirmation(false);
  };
  const closeBattle = (): void => {
    selection = NONE;
    targetChooser.hidden = true;
    closeOptions();
    closeConcedeConfirmation(false);
    minimizedByUser = false;
    minimizedBtn.hidden = true;
    forceClosing = true;
    win.close();
    forceClosing = false;
  };
  onWindowClose = (): void => {
    animator.reset();
    closeOptions();
    closeConcedeConfirmation(false);
    if (!forceClosing && state?.open && state.phase === 'playing') {
      selection = NONE;
      targetChooser.hidden = true;
      minimizedByUser = true;
      minimizedBtn.hidden = false;
      renderStatus();
      return;
    }
    minimizedBtn.hidden = true;
    stopClock();
  };
  minimizedBtn.onclick = () => {
    if (!state?.open) return;
    minimizedByUser = false;
    minimizedBtn.hidden = true;
    win.open();
  };
  closeBtn.onclick = closeBattle;
  win.root.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (!concedeConfirmation.hidden) {
      event.preventDefault();
      closeConcedeConfirmation(true);
      return;
    }
    if (!optionsMenu.hidden) {
      event.preventDefault();
      closeOptions();
      optionsToggle.focus();
      return;
    }
    if (selection.kind === 'none') return;
    event.preventDefault();
    selection = NONE;
    render();
  });
  win.root.addEventListener('click', (event) => {
    if (!optionsMenu.hidden && !optionsEl.contains(event.target as Node)) closeOptions();
  });

  const handle: CardBattleHandle = {
    setState(msg: CardDuelStateMessage): void {
      if (!msg.open) {
        state = msg;
        announcedLogSequence = -1;
        logAnnouncer.textContent = '';
        closeBattle();
        return;
      }
      const newMatch = state?.matchId !== msg.matchId;
      const priorSelection = selection;
      animator.prepare(msg);
      state = msg;
      if (newMatch || msg.phase !== 'mulligan' || msg.mulliganSubmitted) mulliganSelection = new Set();
      if (newMatch || msg.phase === 'ended') {
        minimizedByUser = false;
        minimizedBtn.hidden = true;
      }
      selection =
        !newMatch
        && msg.phase === 'playing'
        && msg.yourTurn
        && priorSelection.kind === 'play-target'
        && msg.hand.some((card) =>
          card.instanceId === priorSelection.instanceId && (card.legalTargets?.length ?? 0) > 0)
          ? priorSelection
          : NONE;
      // Anchor the turn clock to this authoritative push, then run it down locally
      // between pushes. A finished match needs no ticking.
      clockAnchorMs = now();
      clockAnchorSeconds = msg.secondsRemaining;
      if (msg.phase === 'ended') stopClock();
      else clockTimer ??= setInterval(renderStatus, CLOCK_TICK_MS);
      render();
      // Preserve an intentional minimize across state pushes. A new match or its result
      // still opens automatically so the player cannot miss either boundary.
      if (!win.isOpen() && !minimizedByUser) win.open();
    },
    close: closeBattle,
    isOpen: () => win.isOpen(),
  };
  return handle;
}

const MINION_PRESENTATION_CLASSES = [
  'card-battle-advancing',
  'card-battle-striking',
  'card-battle-returning',
  'card-battle-breathing',
  'card-battle-hit',
  'card-battle-casting',
  'card-battle-materializing',
  'card-battle-vanishing',
] as const;

/** Update authoritative minion chrome without replacing an actor that is mid-animation. */
function reconcileMinion(current: HTMLElement, next: HTMLElement): HTMLElement {
  const presentation = MINION_PRESENTATION_CLASSES.filter((name) => current.classList.contains(name));
  const currentArt = current.querySelector<HTMLElement>('.card-art');
  const nextArt = next.querySelector<HTMLElement>('.card-art');
  if (currentArt && nextArt) nextArt.replaceWith(currentArt);

  current.className = next.className;
  current.classList.add(...presentation);
  current.onclick = next.onclick;
  current.dataset.instanceId = next.dataset.instanceId;
  current.dataset.cardId = next.dataset.cardId;
  current.dataset.rarity = next.dataset.rarity;
  current.dataset.side = next.dataset.side;
  current.dataset.facing = next.dataset.facing;
  delete current.dataset.battleGhost;
  current.removeAttribute('aria-hidden');
  current.style.pointerEvents = '';
  current.replaceChildren(...next.childNodes);
  return current;
}

function el(doc: Document, tag: string, className: string): HTMLElement {
  const node = doc.createElement(tag);
  node.className = className;
  return node;
}

/** Keep authored CSS values stable and avoid exposing floating-point noise in the DOM. */
function formatLayoutNumber(value: number): string {
  return Number(value.toFixed(2)).toString();
}

function button(doc: Document, label: string): HTMLButtonElement {
  const b = doc.createElement('button');
  b.type = 'button';
  b.className = 'card-battle-button';
  b.textContent = label;
  return b;
}
